// components/DentalChart.tsx
// Interactive SVG dental chart — FDI Worldwide Numbering System.
// Teeth rendered with 3-D enamel gradient fills and a gum-tissue arch layer.
import React from 'react';
import { Tooltip } from 'antd';
import { DiagramAnnotation } from './DiagramViewer';

interface DentalChartProps {
  annotations: Record<string, DiagramAnnotation>;
  onRegionClick: (regionId: string) => void;
}

const STATUS: Record<string, {
  fill: string;        // used for legend swatch
  gradId: string;      // SVG gradient id applied to tooth body
  stroke: string;
  textColor: string;
  label: string;
}> = {
  normal:  { fill: '#fff',    gradId: 'dc-norm',  stroke: '#c8d4e4', textColor: '#667085', label: 'Normal'  },
  problem: { fill: '#fff1f0', gradId: 'dc-prob',  stroke: '#ff4d4f', textColor: '#cf1322', label: 'Problem' },
  treated: { fill: '#f6ffed', gradId: 'dc-treat', stroke: '#52c41a', textColor: '#389e0d', label: 'Treated' },
  missing: { fill: '#f5f5f5', gradId: 'dc-miss',  stroke: '#8c8c8c', textColor: '#595959', label: 'Missing' },
};

// FDI teeth: upper row L→R, lower row L→R (from chart viewer's perspective)
const UPPER_TEETH = ['18','17','16','15','14','13','12','11','21','22','23','24','25','26','27','28'];
const LOWER_TEETH = ['48','47','46','45','44','43','42','41','31','32','33','34','35','36','37','38'];

// Parabola: y = A*(x − CX)² + K
const CX      = 380;
const A       = 125 / (270 * 270);   // ≈ 0.001714
const UPPER_K = 38;
const LOWER_K = 250;

const X_START = 108;
const X_STEP  = (652 - 108) / 15;    // ≈ 36.3

const getX = (i: number) => X_START + i * X_STEP;
const getY = (i: number, isUpper: boolean) =>
  A * Math.pow(getX(i) - CX, 2) + (isUpper ? UPPER_K : LOWER_K);

const toothShape = (id: string): { w: number; h: number; rx: number } => {
  const n = parseInt(id) % 10;
  if (n <= 2) return { w: 20, h: 23, rx: 10 };  // incisors — slender oval
  if (n === 3) return { w: 22, h: 24, rx: 9  };  // canine
  if (n <= 5)  return { w: 26, h: 26, rx: 7  };  // premolars
  return             { w: 32, h: 28, rx: 6  };    // molars
};

// Arch bezier paths for gum-tissue and guide-track rendering
const archPath = (K: number) => {
  const y0 = A * Math.pow(X_START - CX, 2) + K;
  const ctl = 2 * K - y0;
  return `M ${X_START},${y0} Q ${CX},${ctl} ${X_START + 15 * X_STEP},${y0}`;
};
const UPPER_ARCH_PATH = archPath(UPPER_K);
const LOWER_ARCH_PATH = archPath(LOWER_K);

const SVG_W = 760;
const SVG_H = 430;
const MIDLINE_Y =
  (A * Math.pow(X_START - CX, 2) + UPPER_K + A * Math.pow(X_START - CX, 2) + LOWER_K) / 2;

const DentalChart: React.FC<DentalChartProps> = ({ annotations, onRegionClick }) => {

  const renderTooth = (id: string, i: number, isUpper: boolean) => {
    const ann      = annotations[id];
    const statusKey = ann?.status || 'normal';
    const style    = STATUS[statusKey] ?? STATUS.normal;
    const { w, h, rx } = toothShape(id);
    const cx = getX(i);
    const cy = getY(i, isUpper);
    const x  = cx - w / 2;
    const y  = cy - h / 2;
    const hasNote = !!ann?.note;

    const tip = ann
      ? `Tooth ${id}: ${statusKey}${hasNote ? ' — ' + ann.note : ''}`
      : `Tooth ${id} — click to annotate`;

    return (
      <Tooltip key={id} title={tip} mouseEnterDelay={0.25}>
        <g style={{ cursor: 'pointer' }} onClick={() => onRegionClick(id)}>
          {/* Drop shadow */}
          <rect x={x + 1.5} y={y + 2.5} width={w} height={h} rx={rx}
            fill="rgba(30,42,56,0.12)" />

          {/* Tooth body — 3-D gradient fill */}
          <rect
            x={x} y={y} width={w} height={h} rx={rx}
            fill={`url(#${style.gradId})`}
            stroke={style.stroke}
            strokeWidth={statusKey === 'normal' ? 1.2 : 2.2}
            style={{ transition: 'stroke 0.15s' }}
          />

          {/* Status tint overlay (makes colour more visible) */}
          {statusKey !== 'normal' && (
            <rect x={x} y={y} width={w} height={h} rx={rx}
              fill={style.stroke} fillOpacity={0.1} />
          )}

          {/* Tooth number */}
          <text
            x={cx} y={cy + 4.5}
            textAnchor="middle"
            fontSize={statusKey === 'normal' ? 8.5 : 9.5}
            fontWeight={statusKey === 'normal' ? '500' : '700'}
            fill={style.textColor}
            style={{ fontFamily: 'ui-monospace, monospace', pointerEvents: 'none' }}
          >
            {id}
          </text>

          {/* Note indicator dot */}
          {hasNote && (
            <circle cx={x + w - 4} cy={y + 4} r={3.5} fill="#1677ff" />
          )}
        </g>
      </Tooltip>
    );
  };

  return (
    <div style={{
      background: 'linear-gradient(180deg, #f8fafc 0%, #f0f4f8 100%)',
      borderRadius: 12,
      padding: '12px 8px 16px',
      userSelect: 'none',
    }}>
      {/* Section labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 16px', marginBottom: 2 }}>
        <span style={{ fontSize: 11, color: '#98a2b3', fontWeight: 600, letterSpacing: 0.4 }}>PATIENT RIGHT</span>
        <span style={{ fontSize: 10, color: '#b0b8c4' }}>FDI chart</span>
        <span style={{ fontSize: 11, color: '#98a2b3', fontWeight: 600, letterSpacing: 0.4 }}>PATIENT LEFT</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <svg width={SVG_W} height={SVG_H} viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          style={{ maxWidth: '100%', display: 'block', margin: '0 auto' }}>

          <defs>
            {/* ── 3-D enamel gradients (light from upper-left) ── */}
            <radialGradient id="dc-norm" gradientUnits="objectBoundingBox"
              cx="0.35" cy="0.28" r="0.72">
              <stop offset="0%"   stopColor="#f8fbff" />
              <stop offset="45%"  stopColor="#edf2f9" />
              <stop offset="100%" stopColor="#c6d3e6" />
            </radialGradient>

            <radialGradient id="dc-prob" gradientUnits="objectBoundingBox"
              cx="0.35" cy="0.28" r="0.72">
              <stop offset="0%"   stopColor="#fff5f5" />
              <stop offset="45%"  stopColor="#ffe0e0" />
              <stop offset="100%" stopColor="#ffb0b0" />
            </radialGradient>

            <radialGradient id="dc-treat" gradientUnits="objectBoundingBox"
              cx="0.35" cy="0.28" r="0.72">
              <stop offset="0%"   stopColor="#f3fff3" />
              <stop offset="45%"  stopColor="#d8f8d8" />
              <stop offset="100%" stopColor="#9fe49f" />
            </radialGradient>

            <radialGradient id="dc-miss" gradientUnits="objectBoundingBox"
              cx="0.35" cy="0.28" r="0.72">
              <stop offset="0%"   stopColor="#f7f7f7" />
              <stop offset="45%"  stopColor="#e8e8e8" />
              <stop offset="100%" stopColor="#c4c4c4" />
            </radialGradient>
          </defs>

          {/* ── Gum tissue layer (behind teeth) ───────────────────── */}
          {/* Outer pink gum strip */}
          <path d={UPPER_ARCH_PATH} fill="none" stroke="#f2dce2"
            strokeWidth={50} strokeLinecap="round" opacity={0.5} />
          <path d={LOWER_ARCH_PATH} fill="none" stroke="#f2dce2"
            strokeWidth={50} strokeLinecap="round" opacity={0.5} />

          {/* Inner neutral track (tooth sockets) */}
          <path d={UPPER_ARCH_PATH} fill="none" stroke="#e6ecf5"
            strokeWidth={38} strokeLinecap="round" opacity={0.9} />
          <path d={LOWER_ARCH_PATH} fill="none" stroke="#e6ecf5"
            strokeWidth={38} strokeLinecap="round" opacity={0.9} />

          {/* ── Arch guide lines ──────────────────────────────────── */}
          <path d={UPPER_ARCH_PATH} fill="none" stroke="#d0d9e8"
            strokeWidth={1} strokeLinecap="round" opacity={0.6} />
          <path d={LOWER_ARCH_PATH} fill="none" stroke="#d0d9e8"
            strokeWidth={1} strokeLinecap="round" opacity={0.6} />

          {/* ── Labels ───────────────────────────────────────────── */}
          <text x={SVG_W / 2} y={18} textAnchor="middle"
            fontSize={10} fill="#c0cdd8" fontWeight="700" letterSpacing={1.2}>
            UPPER ARCH
          </text>

          {/* ── Upper teeth ──────────────────────────────────────── */}
          {UPPER_TEETH.map((id, i) => renderTooth(id, i, true))}

          {/* ── Midline divider ───────────────────────────────────── */}
          <line x1={SVG_W / 2 - 1.5} y1={MIDLINE_Y - 18}
                x2={SVG_W / 2 - 1.5} y2={MIDLINE_Y + 18}
            stroke="#d0d5dd" strokeWidth={1.2} strokeDasharray="3,2" />
          <line x1={SVG_W / 2 + 1.5} y1={MIDLINE_Y - 18}
                x2={SVG_W / 2 + 1.5} y2={MIDLINE_Y + 18}
            stroke="#d0d5dd" strokeWidth={1.2} strokeDasharray="3,2" />
          <text x={SVG_W / 2} y={MIDLINE_Y + 4.5}
            textAnchor="middle" fontSize={10} fill="#d0d5dd">·</text>

          {/* ── Lower teeth ──────────────────────────────────────── */}
          {LOWER_TEETH.map((id, i) => renderTooth(id, i, false))}

          <text x={SVG_W / 2} y={SVG_H - 5} textAnchor="middle"
            fontSize={10} fill="#c0cdd8" fontWeight="700" letterSpacing={1.2}>
            LOWER ARCH
          </text>
        </svg>
      </div>

      {/* ── Legend ─────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 18, justifyContent: 'center',
        marginTop: 12, flexWrap: 'wrap', padding: '8px 0 0',
        borderTop: '1px solid #eaecf0',
      }}>
        {Object.entries(STATUS).map(([key, s]) => (
          <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475467' }}>
            <span style={{
              display: 'inline-block', width: 18, height: 18,
              background: s.fill, border: `2px solid ${s.stroke}`,
              borderRadius: 4, flexShrink: 0,
            }} />
            {s.label}
          </span>
        ))}
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#98a2b3' }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: '#1677ff', display: 'inline-block', flexShrink: 0,
          }} />
          Has note
        </span>
      </div>
    </div>
  );
};

export default DentalChart;
